// Draws "active power-up" status rings for YOUR OWN worm's head: one draining
// indicator per active effect, green if it's a buff on you, red if a debuff. No
// icons. Ring-based designs (riffs on concentric arcs / segmented ring / fading
// rings) with a MINIMUM radius so they stay readable when the head is only a few
// pixels (zoomed-out big maps). Pure canvas so it attaches to the moving head in
// both the offline head renderer and the online predicted renderer.

import * as csf from "./canvas-state-functions.js";
import * as gsf from "core/src/core/game-state-functions.js";
import { powerUpDefinitions } from "core/src/core/constants.js";
import { readVariantParam } from "../shared/variant-param";

export interface BarEffect {
  good: boolean; // true = green (self-buff), false = red (opponent debuff)
  frac: number; // 1 -> 0 as the effect's remaining time drains
  slot?: number; // fixed-slot designs (13): stable slot index so bars never reflow
}

const GREEN = "#39e08a";
const RED = "#ff5470";
const T = 1.6; // bar/ring thickness multiplier - bump this to make every design thicker

// --- game-integration helpers (used when wiring into the renderers) -----------
const GOOD_EFFECTS = new Set(["slow", "slim", "quick_turn", "super_jump", "twin", "wall_hack", "clear"]);

// Only speed/size effects carry `duration`; the rest (quick_turn, super_jump,
// wall_hack, drunk, tron_turn, key_switch, ...) only have `timeLeft`, which the
// client's sim doesn't tick during online play. So look the timed length up by the
// power-up name and drain off `effect.time` (the grab gameTime, always set) instead.
const DURATION_BY_NAME: Record<string, number> = {};
for (const k in (powerUpDefinitions as any)) {
  const d = (powerUpDefinitions as any)[k];
  if (d && d.effectDuration) DURATION_BY_NAME[d.name] = d.effectDuration;
}

export function effectToBar(effect: any, renderTime: number): BarEffect | null {
  if (!effect || typeof effect.time !== "number") return null;
  // Wall-hack now shows on the arena walls (see wallhack-designs / map-renderer), not as
  // a head ring, so it no longer produces a bar here.
  if (effect.name === "wall_hack") return null;
  // The post-switcharoonie slow ramp is part of the switch itself (the worm crawls then
  // speeds back up), not a power-up buff/debuff, so it gets no countdown ring. It carries
  // `type` (not `name`) = "switchSlow".
  if (effect.type === "switchSlow" || effect.name === "switchSlow") return null;
  const duration = (typeof effect.duration === "number" && effect.duration > 0)
    ? effect.duration
    : DURATION_BY_NAME[effect.name];
  if (!duration || duration <= 0) return null; // instant effect (clear / switcharoonie): no bar
  const remaining = duration - (renderTime - effect.time);
  if (remaining <= 0) return null;
  return { good: GOOD_EFFECTS.has(effect.name), frac: Math.max(0, Math.min(1, remaining / duration)) };
}

// Which design to draw IN-GAME. Switch with ?fx=1..14 (persists in localStorage);
// default 1. Read fresh (cheap) so the ?fx param always wins immediately.
const OK = (s: string | null): boolean => !!s && /^([1-9]|1[0-4])$/.test(s);
export function getEffectBarsVariant(): number {
  return readVariantParam("fx", "fxVariant", OK, 1);
}
// Capture ?fx at load so it persists even if navigation strips the URL before a draw.
getEffectBarsVariant();

// Fixed-slot bookkeeping (for design 13): each active effect keeps a STABLE slot
// index so its bar never moves once placed. A newly grabbed effect takes the
// lowest free slot; when an effect expires its slot frees for the next one, but
// the effects still on screen stay exactly where they are. Slots 0..2 fill the
// inner ring (1/3 each), 3..5 the next ring out, and so on. State is per worm.
const slotState = new Map<any, Map<string, number>>();
function effectKey(e: any): string { return e.name + ":" + e.time; }
function assignSlots(wormId: any, rawEffects: any[]): number[] {
  let m = slotState.get(wormId);
  if (!m) { m = new Map(); slotState.set(wormId, m); }
  const keys = rawEffects.map(effectKey);
  const live = new Set(keys);
  for (const k of [...m.keys()]) if (!live.has(k)) m.delete(k); // free expired slots
  const used = new Set(m.values());
  return keys.map((k) => {
    let slot = m!.get(k);
    if (slot === undefined) { slot = 0; while (used.has(slot)) slot++; used.add(slot); m!.set(k, slot); }
    return slot;
  });
}

// Renderer convenience: collect a worm's active effects, convert to bars, and draw
// at its head. gameX/gameY/gameSize are GAME units; `scale` maps them to pixels.
export function drawWormEffectBars(
  ctx: CanvasRenderingContext2D,
  gameState: any,
  wormId: any,
  gameX: number,
  gameY: number,
  gameSize: number,
  scale: number,
  renderTime: number,
  preview: boolean = false,
): void {
  // A crashed worm keeps its head drawn at the crash spot, but its power-up rings
  // should vanish (online + local). Skip once the worm is no longer alive.
  const worm = (gsf as any).getWorm(gameState, wormId);
  if (!worm || !worm.alive) { slotState.delete(wormId); return; }
  const raw = (csf as any).getActiveEffects(gameState, renderTime, { wormId });
  const bars: BarEffect[] = [];
  const kept: any[] = [];
  for (const e of raw) { const b = effectToBar(e, renderTime); if (b) { bars.push(b); kept.push(e); } }
  if (!bars.length) { slotState.delete(wormId); return; }
  const slots = assignSlots(wormId, kept);
  slots.forEach((s, i) => { bars[i].slot = s; });
  drawEffectBars(ctx, gameX * scale, gameY * scale, gameSize * scale, bars, getEffectBarsVariant(), preview);
}

// --- drawing ------------------------------------------------------------------
const TAU = Math.PI * 2;
const TOP = -Math.PI / 2;
const SLOTS = 4; // fixed-slot designs: up to this many effects sit at fixed angles (no reflow)

function col(e: BarEffect): string { return e.good ? GREEN : RED; }
function arc(ctx: CanvasRenderingContext2D, cx: number, cy: number, r: number, a0: number, a1: number) {
  ctx.beginPath(); ctx.arc(cx, cy, r, a0, a1); ctx.stroke();
}

const MAX_BARS = 6; // at most this many effects are drawn around one head
// The innermost ring: a little off the head, with a readable minimum.
function ringBaseR(headR: number): number { return Math.max(headR + 7, 13); }

// The furthest any design here can paint from the head's centre, in PIXELS: the
// outermost of MAX_BARS rings at the widest spacing any design uses (6.5, plus its
// 1px offset), half the thickest stroke (5 * T), the widest glow (6) and a pixel
// of antialiasing. The head renderers erase this much around a head every frame
// (the dynamic layer's dirty rects), so it has to be the truth: it used to be a
// separate guess of "26px past the head", which five or six stacked effects
// outgrew, leaving streaks of the outer rings on the arena.
// A design that draws further out than this must raise it.
export function effectBarsExtentPx(headR: number): number {
  return ringBaseR(headR) + 1 + (MAX_BARS - 1) * 6.5 + (5 * T) / 2 + 6 + 1;
}

// cx, cy, headR in PIXELS. A minimum ring radius keeps the indicator readable even
// when headR is tiny (big maps). variant 1..9.
export function drawEffectBars(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  headR: number,
  effects: BarEffect[],
  variant: number,
  preview: boolean = false,
): void {
  if (!effects.length) return;
  const fx = effects.slice(0, MAX_BARS);
  const baseR = ringBaseR(headR);
  ctx.save();
  ctx.lineCap = "round";
  // The lobby GamePreview is a zoomed-in decorative loop, not a real game: the
  // readable-minimum sizing (tuned so rings stay visible on a tiny real-game head)
  // reads as oversized there. Shrink the whole indicator around the head so radii
  // AND stroke scale down together, uniformly for every design.
  if (preview) {
    const k = 0.5;
    ctx.translate(cx, cy);
    ctx.scale(k, k);
    ctx.translate(-cx, -cy);
  }

  switch (variant) {
    // --- concentric sweep arcs (riffs on old #4) ---
    case 1: { // thin sweep
      ctx.lineWidth = 2 * T + 1; // +1px wider stroke
      fx.forEach((e, i) => {
        const r = baseR + 1 + i * 6.5; // +1px off the head, wider gap between rings
        ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, TOP, TOP + TAU * e.frac);
      });
      break;
    }
    case 2: { // thick + glow, no track
      ctx.lineWidth = 3.5 * T;
      fx.forEach((e, i) => {
        const r = baseR + i * 5;
        ctx.shadowBlur = 6; ctx.shadowColor = col(e); ctx.strokeStyle = col(e);
        arc(ctx, cx, cy, r, TOP, TOP + TAU * e.frac);
      });
      ctx.shadowBlur = 0;
      break;
    }
    case 3: { // thicker sweep, wider spacing
      ctx.lineWidth = 3.5 * T;
      fx.forEach((e, i) => {
        const r = baseR + i * 4.5;
        ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, TOP, TOP + TAU * e.frac);
      });
      break;
    }

    // --- segmented ring (riffs on old #5) ---
    case 4: { // EQUAL division: N even segments (symmetric, but reflows as N changes)
      const r = baseR + 2, n = fx.length, gap = 0.3, span = TAU / n - gap;
      ctx.lineWidth = 4 * T;
      fx.forEach((e, i) => {
        const a0 = TOP + i * (TAU / n) + gap / 2;
        ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, a0, a0 + span * e.frac);
      });
      break;
    }
    case 5: { // FIXED slots, drain by arc length (positions never move)
      const r = baseR + 2, gap = 0.3, span = TAU / SLOTS - gap;
      ctx.lineWidth = 4 * T;
      fx.slice(0, SLOTS).forEach((e, i) => {
        const a0 = TOP + i * (TAU / SLOTS) + gap / 2;
        ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, a0, a0 + span * e.frac);
      });
      break;
    }
    case 6: { // FIXED slots, drain by FADE (full-length arcs, only brightness changes)
      const r = baseR + 2, gap = 0.3, span = TAU / SLOTS - gap;
      ctx.lineWidth = 4.5 * T;
      fx.slice(0, SLOTS).forEach((e, i) => {
        const a0 = TOP + i * (TAU / SLOTS) + gap / 2;
        ctx.globalAlpha = 0.22 + 0.78 * e.frac; ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, a0, a0 + span);
      });
      ctx.globalAlpha = 1;
      break;
    }

    // --- fading rings (riffs on old #9) ---
    case 7: { // full rings, opacity = time left
      ctx.lineWidth = 2.5 * T;
      fx.forEach((e, i) => {
        const r = baseR + i * 4;
        ctx.globalAlpha = 0.15 + 0.85 * e.frac; ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, 0, TAU);
      });
      ctx.globalAlpha = 1;
      break;
    }
    case 8: { // full rings, thickness drains
      fx.forEach((e, i) => {
        const r = baseR + i * 4.5;
        ctx.lineWidth = (0.6 + 3.4 * e.frac) * T; ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, 0, TAU);
      });
      break;
    }
    case 9: { // fixed slots, thick + glow, drain length
      const r = baseR + 2, gap = 0.3, span = TAU / SLOTS - gap;
      ctx.lineWidth = 5 * T;
      fx.slice(0, SLOTS).forEach((e, i) => {
        const a0 = TOP + i * (TAU / SLOTS) + gap / 2;
        ctx.shadowBlur = 5; ctx.shadowColor = col(e); ctx.strokeStyle = col(e);
        arc(ctx, cx, cy, r, a0, a0 + span * e.frac);
      });
      ctx.shadowBlur = 0;
      break;
    }

    case 10: { // half circles - green (buffs) on the BOTTOM, red (debuffs) on TOP.
      // Each half-arc drains symmetrically toward its centre (6 o'clock / 12 o'clock);
      // buffs and debuffs each stack outward at their own radii.
      const HALF = Math.PI / 2, BOTTOM = Math.PI / 2; // TOP (module const) = -PI/2
      ctx.lineWidth = 3 * T;
      let g = 0, b = 0;
      fx.forEach((e) => {
        if (e.good) {
          const r = baseR + g * 4; g++;
          ctx.strokeStyle = GREEN; arc(ctx, cx, cy, r, BOTTOM - HALF * e.frac, BOTTOM + HALF * e.frac);
        } else {
          const r = baseR + b * 4; b++;
          ctx.strokeStyle = RED; arc(ctx, cx, cy, r, TOP - HALF * e.frac, TOP + HALF * e.frac);
        }
      });
      break;
    }

    case 11: { // half circles - green (buffs) on the RIGHT, red (debuffs) on the LEFT.
      const HALF = Math.PI / 2, RIGHT = 0, LEFT = Math.PI;
      ctx.lineWidth = 3 * T;
      let g = 0, b = 0;
      fx.forEach((e) => {
        if (e.good) {
          const r = baseR + g * 4; g++;
          ctx.strokeStyle = GREEN; arc(ctx, cx, cy, r, RIGHT - HALF * e.frac, RIGHT + HALF * e.frac);
        } else {
          const r = baseR + b * 4; b++;
          ctx.strokeStyle = RED; arc(ctx, cx, cy, r, LEFT - HALF * e.frac, LEFT + HALF * e.frac);
        }
      });
      break;
    }

    case 12: { // above/below half circles that DESPAWN left -> right (a horizontal
      // sweep: the left end empties first, retracting toward the right), instead of
      // draining symmetrically toward the centre like #10.
      const PI = Math.PI;
      ctx.lineWidth = 3 * T;
      let g = 0, b = 0;
      fx.forEach((e) => {
        if (e.good) {
          const r = baseR + g * 4; g++;
          ctx.strokeStyle = GREEN; arc(ctx, cx, cy, r, 0, PI * e.frac); // bottom, right-anchored
        } else {
          const r = baseR + b * 4; b++;
          ctx.strokeStyle = RED; arc(ctx, cx, cy, r, -PI * e.frac, 0); // top, right-anchored
        }
      });
      break;
    }

    case 13: { // FIXED SLOTS that never reflow: every effect owns a 1/3 wedge and holds
      // that exact position until it expires; a 4th+ effect stacks onto an outer ring.
      // Slot index (assigned in drawWormEffectBars, stable per effect) drives both which
      // ring and which third; bars drain by arc length from a fixed anchor.
      const PER = 3, gap = 0.28, span = TAU / PER - gap;
      const STEP13 = 2.6 * T + 4; // radial gap between ring levels
      ctx.lineWidth = 2.6 * T;
      fx.forEach((e, i) => {
        const slot = typeof e.slot === "number" ? e.slot : i;
        const ring = Math.floor(slot / PER), pos = slot % PER;
        const r = baseR + 3 + ring * STEP13; // +3: sit a little further off the head
        const a0 = TOP + pos * (TAU / PER) + gap / 2;
        ctx.strokeStyle = col(e); arc(ctx, cx, cy, r, a0, a0 + span * e.frac);
      });
      break;
    }

    default: { // 14: like #12 - green (bottom) still despawns left->right, but red (top)
      // despawns RIGHT->LEFT, so the two unzip in opposite directions.
      const PI = Math.PI;
      ctx.lineWidth = 3 * T;
      const r0 = baseR + 4; // push the innermost bar a touch further off the head
      const STEP = 4 * T + 2.5; // radial gap between stacked bars: clear of the thicker stroke
      let g = 0, b = 0;
      fx.forEach((e) => {
        if (e.good) {
          const r = r0 + g * STEP; g++;
          ctx.strokeStyle = GREEN; arc(ctx, cx, cy, r, 0, PI * e.frac); // bottom, right-anchored (same as #12)
        } else {
          const r = r0 + b * STEP; b++;
          ctx.strokeStyle = RED; arc(ctx, cx, cy, r, -PI, -PI + PI * e.frac); // top, LEFT-anchored -> despawns right->left
        }
      });
      break;
    }
  }
  ctx.restore();
}
